// API service для работы с сервисным центром

import {
  mockServiceCatalog,
  mockCustomer,
  generateMockTimeSlots,
  generateMockAppointmentId
} from '../mocks/serviceData';
import { config } from '../config/runtimeConfig.js';

// Читаем из env переменной, по умолчанию используем реальный API
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_SERVICE_API === 'true';
// Fix: strip trailing /api from CAR_API_URL to avoid double /api when building endpoints
// This handles both cases:
// - CAR_API_URL = 'https://app.example.com' (default) → works
// - CAR_API_URL = 'https://app.example.com/api' (widget mode) → still works after stripping
const API_BASE_URL = (config.CAR_API_URL || '').replace(/\/api\/?$/, '');

// Simulate API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Получить каталог услуг из Google Sheets
 * @returns {Promise<Array>} Массив услуг
 */
export async function fetchServiceCatalog() {
  if (USE_MOCK_DATA) {
    await delay(300);
    console.log('[ServiceAPI] Fetching service catalog (mock)');
    return mockServiceCatalog.filter(s => s.active);
  }

  console.log('[ServiceAPI] Fetching service catalog from backend');
  const response = await fetch(`${API_BASE_URL}/api/service-center/catalog`);
  if (!response.ok) {
    console.error('[ServiceAPI] Failed to fetch service catalog:', response.status);
    throw new Error('Failed to fetch service catalog');
  }

  const backendCatalog = await response.json();
  console.log('[ServiceAPI] Service catalog loaded:', backendCatalog.length, 'services');

  return backendCatalog
    .filter(s => s.active)
    .map((service, index) => ({
      serviceId: `srv_${(index + 1).toString().padStart(3, '0')}`,
      serviceName: service.serviceType,
      serviceType: service.serviceType,
      description: service.description,
      requiredHours: service.requiredHours,
      active: service.active,
      standardPrice: 0,
      iconName: getIconForServiceType(service.serviceType),
      sortOrder: index + 1
    }));
}

/**
 * Определяет иконку для типа услуги
 * @param {string} serviceType - Тип услуги
 * @returns {string} Ключ иконки для `components/service/serviceIcons.js`
 */
function getIconForServiceType(serviceType) {
  const iconMap = {
    'Oil Change': 'Droplets',
    'Full Maintenance': 'Wrench',
    'Diagnostic': 'ScanLine',
    'Diagnostics': 'ScanLine',
    'Brake Service': 'CircleStop',
    'Air Conditioning Service': 'Wind',
    'Tire Change': 'Circle',
    'Body Repair': 'CarFront'
  };
  return iconMap[serviceType] || 'Wrench';
}

/**
 * Проверить клиента в CRM по госномеру и телефону
 * @param {string} licensePlate - Госномер
 * @param {string} phone - Телефон
 * @returns {Promise<Object|null>} Данные клиента или null
 */
export async function checkCustomerByCRM(licensePlate, phone) {
  if (USE_MOCK_DATA) {
    await delay(500);
    console.log('[ServiceAPI] Checking customer in CRM (mock):', { licensePlate, phone });

    // Mock: return customer only if matches test data
    if (licensePlate === mockCustomer.licensePlate && phone === mockCustomer.phone) {
      console.log('[ServiceAPI] Customer found (mock)');
      return mockCustomer;
    }

    console.log('[ServiceAPI] Customer not found (mock)');
    return null;
  }

  console.log('[ServiceAPI] Checking customer in CRM:', { licensePlate, phone });
  const response = await fetch(
    `${API_BASE_URL}/api/service-center/check-customer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, licensePlate })
    }
  );

  if (!response.ok) {
    console.error('[ServiceAPI] Failed to check customer:', response.status);
    throw new Error('Failed to check customer');
  }

  const result = await response.json();

  // Backend response: { found: boolean, customer?: {...} }
  if (result.found && result.customer) {
    console.log('[ServiceAPI] Customer found:', result.customer.customerName);

    // Адаптируем данные бэкенда к формату фронтенда
    // Backend: { customerName, phone, licensePlate, carMake, carModel, carYear, lastKnownMileage, serviceHistory, lastVisitDate, lastServiceType }
    // Frontend: { customerId, name, phone, licensePlate, ... }
    return {
      customerId: `cust_${Date.now()}`, // Генерируем временный ID
      name: result.customer.customerName,
      phone: result.customer.phone,
      licensePlate: result.customer.licensePlate,
      carMake: result.customer.carMake,
      carModel: result.customer.carModel,
      carYear: result.customer.carYear,
      lastKnownMileage: result.customer.lastKnownMileage,
      serviceHistory: result.customer.serviceHistory || [],
      lastVisitDate: result.customer.lastVisitDate,
      lastServiceType: result.customer.lastServiceType
    };
  }

  console.log('[ServiceAPI] Customer not found');
  return null;
}

/**
 * Получить доступные слоты времени
 * @param {string} date - Дата в формате YYYY-MM-DD
 * @param {number} duration - Длительность услуги в часах
 * @returns {Promise<Array>} Массив доступных слотов
 */
export async function getAvailableTimeSlots(date, duration) {
  if (USE_MOCK_DATA) {
    await delay(400);
    console.log('[ServiceAPI] Getting available time slots (mock):', { date, duration });

    const slots = generateMockTimeSlots(date, duration);
    console.log('[ServiceAPI] Generated', slots.filter(s => s.available).length, 'available slots (mock)');

    return {
      date,
      slots
    };
  }

  const response = await fetch(
    `${API_BASE_URL}/api/service/slots?date=${date}&duration=${duration}`
  );

  if (!response.ok) throw new Error('Failed to fetch time slots');
  return response.json();
}

/**
 * Создать запись на сервис
 * @param {Object} appointmentData - Данные записи
 * @returns {Promise<Object>} Результат с appointmentId
 */
export async function createAppointment(appointmentData) {
  if (USE_MOCK_DATA) {
    await delay(800);
    const appointmentId = generateMockAppointmentId();

    console.log('[ServiceAPI] Creating appointment (mock):', appointmentData);
    console.log('[ServiceAPI] Mock appointment created:', appointmentId);
    console.log('[ServiceAPI] Mock: Calendar event created');
    console.log('[ServiceAPI] Mock: WhatsApp confirmation sent to', appointmentData.phone);

    return {
      success: true,
      appointmentId,
      appointment: {
        ...appointmentData,
        appointmentId,
        status: 'confirmed',
        createdAt: new Date().toISOString()
      },
      calendarEventId: `cal_mock_${Date.now()}`,
      whatsappSent: true
    };
  }

  const response = await fetch(
    `${API_BASE_URL}/api/service/appointments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(appointmentData)
    }
  );

  if (!response.ok) throw new Error('Failed to create appointment');
  return response.json();
}

/**
 * Получить записи клиента
 * @param {string} licensePlate - Госномер
 * @param {string} phone - Телефон
 * @param {string} statusFilter - Фильтр по статусу
 * @returns {Promise<Array>} Массив записей
 */
export async function getMyAppointments(licensePlate, phone, statusFilter = 'upcoming') {
  if (USE_MOCK_DATA) {
    await delay(400);
    console.log('[ServiceAPI] Getting appointments (mock):', { licensePlate, phone, statusFilter });

    // Mock: return empty for now
    return [];
  }

  const response = await fetch(
    `${API_BASE_URL}/api/service/appointments/my`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ licensePlate, phone, statusFilter })
    }
  );

  if (!response.ok) throw new Error('Failed to fetch appointments');
  return response.json();
}

/**
 * Отменить запись
 * @param {string} appointmentId - ID записи
 * @returns {Promise<Object>} Результат
 */
export async function cancelAppointment(appointmentId) {
  if (USE_MOCK_DATA) {
    await delay(500);
    console.log('[ServiceAPI] Cancelling appointment (mock):', appointmentId);
    console.log('[ServiceAPI] Mock: Calendar event deleted');
    console.log('[ServiceAPI] Mock: WhatsApp cancellation sent');

    return {
      success: true
    };
  }

  const response = await fetch(
    `${API_BASE_URL}/api/service/appointments/${appointmentId}/cancel`,
    { method: 'POST' }
  );

  if (!response.ok) throw new Error('Failed to cancel appointment');
  return response.json();
}

/**
 * Перенести запись
 * @param {string} appointmentId - ID записи
 * @param {string} newDate - Новая дата
 * @param {string} newTime - Новое время
 * @returns {Promise<Object>} Результат
 */
export async function rescheduleAppointment(appointmentId, newDate, newTime) {
  if (USE_MOCK_DATA) {
    await delay(600);
    console.log('[ServiceAPI] Rescheduling appointment (mock):', { appointmentId, newDate, newTime });
    console.log('[ServiceAPI] Mock: Calendar event updated');
    console.log('[ServiceAPI] Mock: WhatsApp reschedule confirmation sent');

    return {
      success: true,
      newCalendarEventId: `cal_mock_${Date.now()}`
    };
  }

  const response = await fetch(
    `${API_BASE_URL}/api/service/appointments/${appointmentId}/reschedule`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: newDate, time: newTime })
    }
  );

  if (!response.ok) throw new Error('Failed to reschedule appointment');
  return response.json();
}

/**
 * Format date for display
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Formatted date (e.g., "November 15, 2024")
 */
export function formatDate(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format time for display
 * @param {string} time24h - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "2:30 PM")
 */
export function formatTime(time24h) {
  const [hours, minutes] = time24h.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}
